import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, ExternalLink, Quote, CheckCircle2, CreditCard, Loader2, X, ShieldCheck } from "lucide-react";
import { ASSETS, TIERS, FAQ } from "../lib/brand";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import PuzzleCalculator from "../components/PuzzleCalculator";
import DonatePinGate from "../components/DonatePinGate";
import { useSearchParams, useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------- Card checkout dialog ----------------
const CardCheckoutDialog = ({ open, onClose, counts, total, onProceed }) => {
  const [form, setForm] = useState({ donor_name: "", donor_email: "", dedication: "", add_to_wall: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onProceed(form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="card-checkout-dialog">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-3xl bg-white p-6 md:p-8 border border-black/5 shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Card payment</div>
            <h3 className="mt-1 font-heading text-2xl font-semibold text-[#1F2937]">
              Donating <span className="tabular-nums">${total.toLocaleString()}</span>
            </h3>
            <div className="mt-1 text-sm text-[#6B7280]">
              {Object.entries(counts).filter(([, v]) => v > 0).map(([k, v]) => `${v} × ${k}`).join(", ") || "your gift"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-full hover:bg-black/5" aria-label="Close">
            <X className="w-5 h-5 text-[#6B7280]" />
          </button>
        </div>

        <div className="mt-5 space-y-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[0.7rem] uppercase tracking-wider font-semibold text-[#6B7280]">Your name (optional)</span>
            <input data-testid="card-input-name" value={form.donor_name} onChange={(e) => set("donor_name", e.target.value)}
                   className="w-full rounded-2xl bg-[#F4F1EA] px-4 py-3 text-sm border border-black/5 focus:outline-none focus:ring-2 focus:ring-[#2196F3]"
                   placeholder="Displayed on your receipt & wall piece" />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[0.7rem] uppercase tracking-wider font-semibold text-[#6B7280]">Email (optional, for receipt)</span>
            <input type="email" data-testid="card-input-email" value={form.donor_email} onChange={(e) => set("donor_email", e.target.value)}
                   className="w-full rounded-2xl bg-[#F4F1EA] px-4 py-3 text-sm border border-black/5 focus:outline-none focus:ring-2 focus:ring-[#2196F3]"
                   placeholder="you@example.com" />
          </label>
          <label className="flex items-start gap-3 mt-2 cursor-pointer">
            <input type="checkbox" data-testid="card-input-wall" checked={form.add_to_wall} onChange={(e) => set("add_to_wall", e.target.checked)}
                   className="mt-1 w-4 h-4 accent-[#2196F3]" />
            <span className="text-sm text-[#1F2937]">
              Add my piece to the Community Wall
              <span className="block text-xs text-[#6B7280]">Your name (and dedication, if you add one) will appear as a puzzle piece on the wall.</span>
            </span>
          </label>
          {form.add_to_wall && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[0.7rem] uppercase tracking-wider font-semibold text-[#6B7280]">Dedication (optional)</span>
              <input data-testid="card-input-dedication" value={form.dedication} onChange={(e) => set("dedication", e.target.value)}
                     className="w-full rounded-2xl bg-[#F4F1EA] px-4 py-3 text-sm border border-black/5 focus:outline-none focus:ring-2 focus:ring-[#2196F3]"
                     placeholder="e.g. In honor of my son Marcus" />
            </label>
          )}
        </div>

        <div className="mt-5 flex items-center gap-2 text-xs text-[#6B7280]">
          <ShieldCheck className="w-4 h-4 text-[#4CAF50]" strokeWidth={1.6} />
          Secure checkout by Stripe. We never see your card.
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#1F2937] hover:bg-black/5">Back</button>
          <button type="submit" disabled={loading} data-testid="card-continue-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#2196F3] hover:bg-[#1e88e5] text-white px-6 py-3 text-sm font-semibold disabled:opacity-60">
            {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>) : (<><CreditCard className="w-4 h-4" /> Continue to Stripe</>)}
          </button>
        </div>
      </form>
    </div>
  );
};

// ---------------- Post-payment status card ----------------
const PaymentStatusBanner = ({ sessionId, onClear }) => {
  const [state, setState] = useState({ loading: true, status: null, amount: 0, error: null });
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled.current) return;
      try {
        const { data } = await axios.get(`${API}/donation/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, status: "paid", amount: data.amount_total, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ loading: false, status: "expired", amount: data.amount_total, error: null });
          return;
        }
        if (attempts++ >= 8) {
          setState({ loading: false, status: "pending", amount: data.amount_total, error: null });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ loading: false, status: null, amount: 0, error: "Could not verify payment" });
      }
    };
    poll();
    return () => { cancelled.current = true; };
  }, [sessionId]);

  const s = state.status;
  const paid = s === "paid";
  const color = paid ? "#4CAF50" : s === "expired" ? "#E53935" : "#FFB300";
  const title = state.loading ? "Verifying your payment…"
    : paid ? "Thank you — your gift has been received!"
    : s === "expired" ? "Payment session expired"
    : s === "pending" ? "Payment is being processed"
    : "Payment status unknown";
  const body = state.loading ? "Please hold on while we confirm with Stripe."
    : paid ? `Your donation of $${state.amount.toLocaleString()} has been recorded. A piece has been added to our Community Wall.`
    : s === "expired" ? "Your Stripe session expired before payment. No card was charged."
    : s === "pending" ? "Your card was accepted; we're just waiting on final confirmation. This can take a few minutes."
    : (state.error || "We couldn't verify your payment. Please contact us with your session ID.");

  return (
    <div data-testid="payment-status-banner" className="rounded-3xl border border-black/5 bg-white p-6 md:p-8 flex items-start gap-5">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15`, border: `1px solid ${color}55` }}>
        {state.loading
          ? <Loader2 className="w-5 h-5 animate-spin" style={{ color }} />
          : paid ? <CheckCircle2 className="w-5 h-5" style={{ color }} />
          : <Heart className="w-5 h-5" style={{ color }} />}
      </div>
      <div className="flex-1">
        <div className="font-heading text-xl font-semibold text-[#1F2937]" data-testid="payment-status-title">{title}</div>
        <div className="mt-1 text-sm text-[#4B5563] leading-relaxed">{body}</div>
        <div className="mt-3 text-[0.7rem] text-[#9CA3AF] font-mono break-all">session: {sessionId}</div>
      </div>
      <button onClick={onClear} className="p-1 rounded-full hover:bg-black/5" aria-label="Dismiss">
        <X className="w-5 h-5 text-[#6B7280]" />
      </button>
    </div>
  );
};

export default function Donate() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", tier: "blue", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [calcTotal, setCalcTotal] = useState(0);
  const [calcCounts, setCalcCounts] = useState({ green: 0, yellow: 0, red: 0, blue: 1 });
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [cancelledNotice, setCancelledNotice] = useState(false);
  const qrRef = useRef(null);
  const paymentBannerRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled");

  useEffect(() => {
    if (cancelled) {
      setCancelledNotice(true);
      // Remove the query param after showing notice
      const t = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 6000);
      return () => clearTimeout(t);
    }
  }, [cancelled, setSearchParams]);

  useEffect(() => {
    if (sessionId) {
      // Scroll to banner area
      setTimeout(() => {
        paymentBannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [sessionId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goToPayment = ({ counts, total }) => {
    setCalcCounts(counts);
    setCalcTotal(total);
    const summary = Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${v} × ${k}`)
      .join(", ");
    setForm((f) => ({
      ...f,
      message: (f.message ? f.message + "\n\n" : "") + `[Calculator selection] Total: $${total}. Pieces: ${summary}.`,
    }));
    qrRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openCardDialog = ({ counts, total }) => {
    setCalcCounts(counts);
    setCalcTotal(total);
    setCardDialogOpen(true);
  };

  const proceedToStripe = useCallback(async (donorInfo) => {
    setCardLoading(true);
    try {
      const payload = {
        counts: calcCounts,
        origin_url: window.location.origin,
        donor_name: donorInfo.donor_name || null,
        donor_email: donorInfo.donor_email || null,
        dedication: donorInfo.dedication || null,
        add_to_wall: !!donorInfo.add_to_wall,
      };
      const { data } = await axios.post(`${API}/donation/checkout`, payload);
      if (!data.url) throw new Error("no url");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setCardLoading(false);
    }
  }, [calcCounts]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.tier) {
      toast.error("Please fill in your name, email, and tier.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partnership`, form);
      setDone(true);
      toast.success("Thank you! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", organization: "", tier: "blue", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-donate">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Donate · Partner</div>
        <h1 className="mt-4 font-heading font-bold tracking-tighter text-[#1F2937] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
          Your piece<br />
          <span className="brush-underline brush-red">matters.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#4B5563] leading-relaxed">
          Every contribution helps us build a permanent home for Autism families in Marion, Arkansas — and a
          sensory-friendly event hall where every child can celebrate.
        </p>
      </section>

      {/* Payment status / cancellation banner */}
      {(sessionId || cancelledNotice) && (
        <section ref={paymentBannerRef} className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 pt-2 pb-4">
          {sessionId ? (
            <PaymentStatusBanner
              sessionId={sessionId}
              onClear={() => { setSearchParams({}, { replace: true }); navigate("/donate", { replace: true }); }}
            />
          ) : (
            <div data-testid="payment-cancelled-banner" className="rounded-3xl border border-black/5 bg-white p-6 flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#E53935]/10 border border-[#E53935]/40 flex items-center justify-center">
                <X className="w-5 h-5 text-[#E53935]" />
              </div>
              <div>
                <div className="font-heading text-lg font-semibold text-[#1F2937]">Checkout cancelled</div>
                <div className="text-sm text-[#6B7280] mt-1">No card was charged. Feel free to try again — every piece matters.</div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Puzzle Calculator */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-6">
        <PuzzleCalculator
          onSelectionChange={({ counts, total }) => { setCalcCounts(counts); setCalcTotal(total); }}
          onCheckout={goToPayment}
          onCardCheckout={openCardDialog}
        />
        <div className="mt-3 text-xs text-[#6B7280] text-center">
          Prefer Cash App or a single tier? Scroll down for the QR code and partnership form.
        </div>
      </section>

      {/* Cash App + Tiers */}
      <section ref={qrRef} className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-10 grid lg:grid-cols-12 gap-8" data-testid="donate-payment-section">
        <div className="lg:col-span-5">
          <div className="rounded-3xl bg-white border border-black/5 p-8">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-[#4CAF50]/10 border border-[#4CAF50]/40 flex items-center justify-center">
                <Heart className="w-5 h-5 text-[#4CAF50]" strokeWidth={1.6} />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-[#1F2937]">Donate via Cash App</h3>
                <p className="text-sm text-[#6B7280]">Scan the QR code to give securely.</p>
              </div>
            </div>
            <div className="mt-6 rounded-2xl bg-[#F4F1EA] p-4 flex items-center justify-center">
              <img src={ASSETS.qr} alt="Cash App donation QR code"
                   data-testid="cashapp-qr-image"
                   className="w-64 h-64 object-cover rounded-xl bg-white" />
            </div>
            <p className="mt-5 text-xs text-[#6B7280] leading-relaxed">
              Open your Cash App, tap the QR scanner, and point it at this code. Choose your amount and
              celebrate — you've just become a piece of our puzzle.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Partnership Tiers</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1F2937]">Pick your puzzle piece.</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {TIERS.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`tier-select-${t.key}`}
                onClick={() => set("tier", t.key)}
                className={`relative text-left rounded-3xl border p-7 overflow-hidden transition ${
                  form.tier === t.key ? "border-[#1F2937] shadow-sm -translate-y-0.5" : "border-black/5 hover:-translate-y-0.5"
                }`}
                style={{ background: t.bgTint }}
              >
                <div className="tier-stripe" style={{ backgroundColor: t.color }} />
                <div className="flex items-baseline justify-between">
                  <div className="text-[0.7rem] tracking-[0.2em] font-semibold uppercase" style={{ color: t.color }}>{t.name.split(" ")[0]}</div>
                  <div className="font-heading text-3xl font-bold text-[#1F2937]">${t.amount}</div>
                </div>
                <div className="mt-1 text-sm text-[#6B7280]">{t.name}</div>
                <div className="mt-4 flex items-start gap-2 text-sm text-[#4B5563]">
                  <Quote className="w-4 h-4 mt-0.5 shrink-0" style={{ color: t.color }} strokeWidth={1.6} />
                  <span>"{t.quote}" — {t.author}</span>
                </div>
                {form.tier === t.key && (
                  <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-xs font-semibold text-[#1F2937]">
                    <CheckCircle2 className="w-4 h-4 text-[#4CAF50]" /> Selected
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Form */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-12">
        <div className="rounded-3xl bg-[#1F2937] text-white p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#2196F3]/25 blur-3xl" />
          <div className="absolute -left-16 -bottom-16 w-72 h-72 rounded-full bg-[#FFB300]/15 blur-3xl" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FFB300]">Become a Partner</div>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Tell us about your partnership.</h2>
            <p className="mt-3 text-white/75 max-w-xl">Choose a tier and share a few details — our director Chatoria will personally follow up.</p>

            {done ? (
              <div data-testid="partnership-success" className="mt-8 rounded-2xl bg-white text-[#1F2937] p-6 flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#4CAF50] mt-0.5" />
                <div>
                  <div className="font-heading text-lg font-semibold">Thank you, partner!</div>
                  <div className="text-sm text-[#4B5563] mt-1">We received your inquiry. We'll be in touch within a few business days.</div>
                  <button onClick={() => setDone(false)} data-testid="partnership-new-btn" className="mt-4 text-sm text-[#2196F3] font-semibold hover:underline">Submit another</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 grid md:grid-cols-2 gap-4" data-testid="partnership-form">
                <Field label="Your name *" data-testid="partnership-name">
                  <input data-testid="partnership-input-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
                         className={inputCls} placeholder="Jane Doe" />
                </Field>
                <Field label="Email *">
                  <input data-testid="partnership-input-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                         className={inputCls} placeholder="jane@example.com" />
                </Field>
                <Field label="Phone">
                  <input data-testid="partnership-input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                         className={inputCls} placeholder="(870) 555-0199" />
                </Field>
                <Field label="Organization">
                  <input data-testid="partnership-input-org" value={form.organization} onChange={(e) => set("organization", e.target.value)}
                         className={inputCls} placeholder="Your business or church" />
                </Field>
                <Field label="Selected tier" className="md:col-span-2">
                  <select data-testid="partnership-input-tier" value={form.tier} onChange={(e) => set("tier", e.target.value)} className={inputCls}>
                    {TIERS.map((t) => <option key={t.key} value={t.key}>{`${t.name} — $${t.amount}`}</option>)}
                    <option value="custom">{`Custom amount / In-kind partnership`}</option>
                  </select>
                </Field>
                <Field label="A note for us" className="md:col-span-2">
                  <textarea data-testid="partnership-input-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)}
                            className={inputCls} placeholder="Anything you'd like Chatoria to know…" />
                </Field>
                <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-4 mt-2">
                  <a href="https://www.facebook.com/share/1DGrahrNKh/" target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-white">
                    More on Facebook <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button type="submit" disabled={submitting} data-testid="partnership-submit-btn"
                          className="inline-flex items-center gap-2 rounded-full bg-[#FFB300] hover:bg-[#ffa000] text-[#1F2937] px-7 py-3.5 text-sm font-semibold disabled:opacity-60">
                    {submitting ? "Sending…" : "Submit partnership inquiry"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">FAQ</div>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1F2937]">Frequently asked questions.</h2>
        <Accordion type="single" collapsible className="mt-8 divide-y divide-black/5 rounded-3xl border border-black/5 bg-white" data-testid="faq-accordion">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="px-6 border-b-0">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-heading text-base sm:text-lg font-semibold text-[#1F2937] hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#4B5563] leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Card checkout dialog */}
      <CardCheckoutDialog
        open={cardDialogOpen}
        onClose={() => !cardLoading && setCardDialogOpen(false)}
        counts={calcCounts}
        total={calcTotal}
        onProceed={proceedToStripe}
      />
    </div>
  );
}

const inputCls = "w-full rounded-2xl bg-white text-[#1F2937] placeholder:text-[#9CA3AF] px-4 py-3 text-sm border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#2196F3]";

const Field = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-1.5 ${className}`}>
    <span className="text-xs font-semibold uppercase tracking-wider text-white/70">{label}</span>
    {children}
  </label>
);
